import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Facebook,
  Instagram,
} from "lucide-react";
import { useSettings } from "../../context/SettingsContext";
import api, { imgUrl } from "../../lib/api";

export default function Footer() {
  const { settings } = useSettings();
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api
      .get("/pages")
      .then(({ data }) => setPages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const c = settings?.contact || {};
  const s = settings?.social || {};

  const shopName =
    settings?.shop_name || "Φαρμακείο Κατιρτζόγλου";

  return (
    <footer className="bg-[#1E3A2F] text-[#CBD9B6] mt-20">

      {/* =====================================================
          FOOTER CONTENT
      ====================================================== */}

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">

        {/* =====================================================
            ABOUT
        ====================================================== */}

        <div>
          <div className="flex items-center gap-3 mb-4">

            <img
              src={imgUrl(settings?.logo || "/logo.jpeg")}
              alt={`${shopName} - Λογότυπο`}
              className="h-12 w-12 rounded-md object-cover"
              loading="lazy"
            />

            <span className="font-serif-display text-xl text-white">
              {shopName}
            </span>

          </div>

          <p className="text-sm leading-relaxed opacity-80">
            {settings?.footer_about ||
              settings?.shipping_info ||
              "Φαρμακείο Κατιρτζόγλου – Υγεία, φροντίδα και ευεξία με υπευθυνότητα και προσωπική εξυπηρέτηση."}
          </p>
        </div>

        {/* =====================================================
            NAVIGATION
        ====================================================== */}

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
            Πλοήγηση
          </h4>

          <ul className="space-y-2 text-sm">

            <li>
              <Link
                to="/"
                className="hover:text-white transition-colors"
              >
                Αρχική
              </Link>
            </li>

            <li>
              <Link
                to="/shop"
                className="hover:text-white transition-colors"
              >
                Όλα τα προϊόντα
              </Link>
            </li>

            <li>
              <Link
                to="/cart"
                className="hover:text-white transition-colors"
              >
                Καλάθι
              </Link>
            </li>

            {pages.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/page/${p.slug}`}
                  className="hover:text-white transition-colors"
                >
                  {p.title}
                </Link>
              </li>
            ))}

          </ul>
        </div>

        {/* =====================================================
            CONTACT + SOCIAL
        ====================================================== */}

        <div>

          <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
            Επικοινωνία
          </h4>

          <ul className="space-y-3 text-sm">

            {c.phone && (
              <li className="flex items-center gap-2">

                <Phone className="h-4 w-4 shrink-0" />

                <a
                  href={`tel:${c.phone}`}
                  className="hover:text-white transition-colors"
                >
                  {c.phone}
                </a>

              </li>
            )}

            {c.email && (
              <li className="flex items-center gap-2">

                <Mail className="h-4 w-4 shrink-0" />

                <a
                  href={`mailto:${c.email}`}
                  className="hover:text-white transition-colors break-all"
                >
                  {c.email}
                </a>

              </li>
            )}

            {c.address && (
              <li className="flex items-start gap-2">

                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />

                <span>
                  {c.address}
                </span>

              </li>
            )}

            {c.hours && (
              <li className="flex items-start gap-2">

                <Clock className="h-4 w-4 mt-0.5 shrink-0" />

                <span>
                  {c.hours}
                </span>

              </li>
            )}

          </ul>

          {/* =====================================================
              SOCIAL
          ====================================================== */}

          {(s.facebook || s.instagram) && (
            <div className="mt-6">

              <h4 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">
                Ακολουθήστε μας
              </h4>

              <div className="flex gap-3">

                {s.facebook && (
                  <a
                    href={s.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="p-2 border border-[#CBD9B6]/30 rounded-sm hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <Facebook
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  </a>
                )}

                {s.instagram && (
                  <a
                    href={s.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="p-2 border border-[#CBD9B6]/30 rounded-sm hover:bg-white/10 hover:text-white transition-colors"
                  >
                    <Instagram
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  </a>
                )}

              </div>

            </div>
          )}

        </div>

      </div>

      {/* =====================================================
          COPYRIGHT
      ====================================================== */}

      <div className="border-t border-white/10 py-6 text-center text-xs opacity-70">

        {settings?.copyright
          ? settings.copyright
          : `© ${new Date().getFullYear()} ${shopName}. Με επιφύλαξη παντός δικαιώματος.`}

      </div>

    </footer>
  );
}